import { useState, useContext } from "react";
import axios from "axios";
import { AuthContext } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { Helmet } from 'react-helmet-async';
import axiosInstance from "../utils/axiosInstance";

const Login = () => {
  const { login } = useContext(AuthContext);
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); // start loading
    try {
      const res = await axiosInstance.post(
        `${process.env.REACT_APP_API_BASE}/api/auth/login`,
        form,
        {
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': axios.defaults.headers.common['X-CSRF-Token'],
          },
          withCredentials: true,
        }
      );
      login(res.data.token, res.data.userId);
      toast.success("Login successful!");
      navigate("/profile");
    } catch (err) {
      const errorMessage = err.response?.data?.error || "Login failed. Please try again.";
      toast.error(errorMessage);
      console.error("Login error:", err);
    } finally {
      setLoading(false); // end loading
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-100 via-white to-indigo-200 flex items-center justify-center px-4">
      <Helmet>
        <title>Login - PairLingo</title>
        <meta name="description" content="Speak with perfect language partner on PairLingo." />
        <meta name="keywords" content="language learning, pairlingo, chat, match, speaking chat, german language partner chat, language speaking practice" />
      </Helmet>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-8">
        <h2 className="text-xl font-bold mb-4">Login</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            className="input"
            name="email"
            placeholder="Email"
            onChange={handleChange}
            required
          />
          <input
            className="input"
            name="password"
            type="password"
            placeholder="Password"
            onChange={handleChange}
            required
          />
          <button
            className={`w-full px-4 py-2 rounded text-white text-sm ${
              loading ? "bg-indigo-400 cursor-not-allowed" : "bg-indigo-600 hover:bg-indigo-500"
            }`}
            type="submit"
            disabled={loading}
          >
            {loading ? "Logging in..." : "Login"}
          </button>
        </form>
        <div className="mt-4 text-center">
          <button
            type="button"
            className="text-indigo-600 hover:underline text-sm"
            onClick={() => setShowForgot(true)}
          >
            Forgot Password?
          </button>
        </div>
        {/* Forgot Password Modal */}
        {showForgot && (
          <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-sm relative">
              <button
                className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
                onClick={() => setShowForgot(false)}
              >
                &times;
              </button>
              <h3 className="text-lg font-semibold mb-2">Reset Password</h3>
              <p className="text-sm mb-4">Enter your email address. We'll send you a temporary password.</p>
              <input
                type="email"
                className="w-full px-3 py-2 border rounded mb-3"
                placeholder="Email"
                value={forgotEmail}
                onChange={e => setForgotEmail(e.target.value)}
                required
              />
              <button
                className={`w-full bg-indigo-600 text-white py-2 rounded font-semibold hover:bg-indigo-500 ${forgotLoading ? 'opacity-60 cursor-not-allowed' : ''}`}
                onClick={async () => {
                  if (!forgotEmail) {
                    toast.error("Please enter your email");
                    return;
                  }
                  setForgotLoading(true);
                  try {
                    await axiosInstance.post(`${process.env.REACT_APP_API_BASE}/api/auth/forgot-password`, { email: forgotEmail });
                    toast.success("Temporary password will be sent to your email, if this email exists.", { duration: 6000 });
                    setShowForgot(false);
                    setForgotEmail("");
                  } catch (err) {
                    toast.error("Failed to send temporary password");
                  } finally {
                    setForgotLoading(false);
                  }
                }}
                disabled={forgotLoading}
              >
                {forgotLoading ? "Sending..." : "Send Temporary Password"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Login;